import { Check, ChevronLeft, Plus, X } from '@tamagui/lucide-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getTokenValue, Heading, Token, XStack, YStack } from 'tamagui';
import { hexToRGBA } from 'tamagui-config';

import { DraggableScrollView } from './DraggableScrollView';
import { useDraggingContext } from '../../hooks';

interface WidgetsWrapperProps {
  children: React.ReactNode;
  onStartDragging?: () => void;
  onCancelOrder?: () => void;
  onSaveOrder?: () => void;
}

export const WidgetsWrapper = ({
  children,
  onStartDragging,
  onCancelOrder,
  onSaveOrder,
}: WidgetsWrapperProps) => {
  const navigate = useNavigate();
  const { isDraggable } = useDraggingContext();
  const { t } = useTranslation();

  const gradientStartColor = getTokenValue('$hgPrimaryGradientStart' as Token, 'color');
  const gradientEndColor = getTokenValue('$hgPrimaryGradientEnd' as Token, 'color');
  const elevatedShadowColor = getTokenValue('$hgPrimary' as Token, 'color');

  const gradient = `linear-gradient(106deg, ${gradientStartColor} 0%, ${gradientEndColor} 100%)`;
  const boxShadow = `0px 4px 24px 0px ${hexToRGBA(elevatedShadowColor, 0.25)}`;

  const handleBackPress = () => {
    try {
      navigate(-1);
    } catch {
      navigate('/');
    }
  };

  return (
    <XStack flex={1} width="100%">
      {!isDraggable ? (
        <XStack
          width={104}
          backgroundImage={gradient}
          boxShadow={boxShadow}
          borderRadius={100}
          alignItems="center"
          justifyContent="center"
          margin="$5"
          onPress={handleBackPress}
          pressStyle={{ opacity: 0.8 }}
          cursor="pointer"
          userSelect="none">
          <ChevronLeft size={48} color="$white" />
        </XStack>
      ) : (
        <YStack gap="$5" margin="$5" alignItems="center" justifyContent="center">
          <XStack
            flex={1}
            width={104}
            backgroundImage={gradient}
            borderRadius={100}
            alignItems="center"
            justifyContent="center"
            onPress={onSaveOrder}
            pressStyle={{ opacity: 0.8 }}>
            <Check size={48} color="$white" />
          </XStack>
          <XStack
            flex={1}
            width={104}
            backgroundColor="$background"
            borderColor="$hgPrimary"
            borderWidth={2}
            borderRadius={100}
            alignItems="center"
            justifyContent="center"
            onPress={onCancelOrder}
            pressStyle={{ opacity: 0.8 }}>
            <X size={48} color="$hgPrimary" />
          </XStack>
        </YStack>
      )}
      <DraggableScrollView>
        <XStack>{children}</XStack>
        {!isDraggable && (
          <YStack
            width={141}
            gap="$3.5"
            borderColor="$hgPrimary"
            borderWidth={2}
            borderRadius={100}
            alignItems="center"
            justifyContent="center"
            marginLeft={0}
            marginRight="$5"
            onPress={onStartDragging}
            pressStyle={{ opacity: 0.8 }}>
            <Plus size={48} color="$hgPrimary" />
            <Heading
              size="$5"
              color="$hgPrimary"
              textTransform="capitalize"
              textAlign="center"
              lineHeight="$4">
              {t('common.addWidget')}
            </Heading>
          </YStack>
        )}
      </DraggableScrollView>
    </XStack>
  );
};
